/**
 * Daily Alignment Signal
 * A quick state check when users open the app.
 * Takes 2-3 seconds to complete.
 * 
 * States: Overwhelmed → Stressed → Neutral → Calm → Inspired → Grateful
 * 
 * Based on selection, the app recommends activities:
 * - Stressed/Overwhelmed → Quick Reset, Breath regulation, Calming audio
 * - Neutral → Today's practice, Balance restoration
 * - Calm → Hero Journey challenge, Focus mode
 * - Inspired → Contribution challenge, Creative flow, Sharing
 * - Grateful → Share moment, Gratitude reflection, Send appreciation
 */

import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, 
  Waves, 
  Wind, 
  Sun, 
  Sparkles, 
  Flame,
  ArrowRight,
  Heart,
  Brain,
  Mountain,
  Music,
  Mic,
  Timer,
  RefreshCw,
  Send,
  PenLine,
  Gift
} from "lucide-react";

// Alignment states with colors and recommendations
const ALIGNMENT_STATES = [
  {
    id: "overwhelmed",
    label: "Overwhelmed",
    color: "from-red-500 to-rose-600",
    bgColor: "bg-red-500/20",
    borderColor: "border-red-500/40",
    textColor: "text-red-400",
    icon: Waves,
    emoji: "🌫",
    message: "Let's bring you back to center.",
    journeyMessage: "Even heroes need to pause and breathe.",
    recommendations: [
      { id: "reset", label: "Quick Reset", sublabel: "2-minute nervous system reset", icon: RefreshCw, route: "/private-suite", action: "reset" },
      { id: "breathe", label: "Breath Regulation", sublabel: "Box breathing exercise", icon: Wind, route: "/private-suite", action: "breathe" },
      { id: "calm-audio", label: "Calming Audio", sublabel: "Soothing soundscape", icon: Music, route: "/private-suite", action: "audio" }
    ]
  },
  {
    id: "stressed",
    label: "Stressed",
    color: "from-orange-500 to-amber-600",
    bgColor: "bg-orange-500/20",
    borderColor: "border-orange-500/40",
    textColor: "text-orange-400",
    icon: Flame,
    emoji: "🌧",
    message: "Time to release the tension.",
    journeyMessage: "Awareness is the first step of transformation.",
    recommendations: [
      { id: "reset", label: "Quick Reset", sublabel: "Release & regulate", icon: RefreshCw, route: "/private-suite", action: "reset" },
      { id: "breathe", label: "Deep Breathing", sublabel: "4-7-8 calming breath", icon: Wind, route: "/private-suite", action: "breathe" },
      { id: "affirmation", label: "Grounding Words", sublabel: "Centering affirmation", icon: Mic, route: "/private-suite", action: "affirmation" }
    ]
  },
  {
    id: "neutral",
    label: "Neutral",
    color: "from-yellow-500 to-amber-500",
    bgColor: "bg-yellow-500/20",
    borderColor: "border-yellow-500/40",
    textColor: "text-yellow-400",
    icon: Sun,
    emoji: "🌤",
    message: "A steady foundation to build from.",
    journeyMessage: "From stillness, your path becomes clearer.",
    recommendations: [
      { id: "journey", label: "Today's Practice", sublabel: "Continue your Hero's Journey", icon: Mountain, route: "/hero-journey", action: "journey" },
      { id: "focus", label: "Focus Session", sublabel: "Deep work mode", icon: Timer, route: "/private-suite", action: "focus" },
      { id: "breathe", label: "Energizing Breath", sublabel: "Wim Hof style activation", icon: Wind, route: "/private-suite", action: "breathe" }
    ]
  },
  {
    id: "calm",
    label: "Calm",
    color: "from-emerald-500 to-teal-500",
    bgColor: "bg-emerald-500/20",
    borderColor: "border-emerald-500/40",
    textColor: "text-emerald-400",
    icon: Heart,
    emoji: "☀️",
    message: "Beautiful. You're in a powerful state.",
    journeyMessage: "From this calm, your path becomes clearer.",
    recommendations: [
      { id: "journey", label: "Hero's Journey", sublabel: "Continue your transformation", icon: Mountain, route: "/hero-journey", action: "journey" },
      { id: "challenge", label: "Today's Challenge", sublabel: "Push your edge", icon: Flame, route: "/challenges", action: "challenge" },
      { id: "creative", label: "Creative Flow", sublabel: "Express yourself", icon: Brain, route: "/private-suite", action: "creative" }
    ]
  },
  {
    id: "inspired",
    label: "Inspired",
    color: "from-blue-500 to-indigo-600",
    bgColor: "bg-blue-500/20",
    borderColor: "border-blue-500/40",
    textColor: "text-blue-400",
    icon: Sparkles,
    emoji: "✨",
    message: "Channel this energy into action.",
    journeyMessage: "The light within you seeks expression.",
    recommendations: [
      { id: "challenge", label: "Leadership Action", sublabel: "Step into your power", icon: Flame, route: "/challenges", action: "leadership" },
      { id: "share", label: "Share Your Journey", sublabel: "Inspire others", icon: Heart, route: "/hero-journey/share", action: "share" },
      { id: "creative", label: "Creative Practice", sublabel: "Create something new", icon: Sparkles, route: "/private-suite", action: "creative" }
    ]
  },
  {
    id: "grateful",
    label: "Grateful",
    color: "from-amber-400 to-yellow-500",
    bgColor: "bg-amber-400/20",
    borderColor: "border-amber-400/40",
    textColor: "text-amber-400",
    icon: Gift,
    emoji: "💛",
    isGolden: true, // Special golden glow for gratitude
    message: "Beautiful. Gratitude expands everything.",
    journeyMessage: "When we appreciate what is present, life reveals even more.",
    recommendations: [
      { id: "share-moment", label: "Share a Moment", sublabel: "Let someone know you're grateful", icon: Send, route: "/hero-journey/share", action: "share" },
      { id: "gratitude-reflection", label: "Gratitude Reflection", sublabel: "Write what you appreciate", icon: PenLine, route: "/private-suite", action: "gratitude" },
      { id: "send-appreciation", label: "Send Appreciation", sublabel: "Touch someone's day", icon: Gift, route: "/hero-journey/share", action: "appreciation" }
    ]
  }
];

// Helper to calculate pattern insights from alignment history
const getAlignmentInsights = () => {
  try {
    const history = JSON.parse(localStorage.getItem('reclaim_alignment_history') || '[]');
    const last7Days = history.filter(h => {
      const date = new Date(h.timestamp);
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
      return diffDays < 7;
    });
    
    if (last7Days.length < 2) return null;
    
    // Count states in last 7 days
    const stateCounts = {};
    last7Days.forEach(h => {
      stateCounts[h.state] = (stateCounts[h.state] || 0) + 1;
    });
    
    // Find most common state
    const sortedStates = Object.entries(stateCounts).sort((a, b) => b[1] - a[1]);
    if (sortedStates.length > 0 && sortedStates[0][1] >= 2) {
      const topState = ALIGNMENT_STATES.find(s => s.id === sortedStates[0][0]);
      if (topState) {
        return {
          state: topState,
          count: sortedStates[0][1],
          message: `You've felt ${topState.label.toLowerCase()} ${sortedStates[0][1]} times this week`
        };
      }
    }
    
    // Check for gratitude ripples
    const gratitudeShares = last7Days.filter(h => h.state === 'grateful').length;
    if (gratitudeShares > 0) {
      return {
        isGratitude: true,
        count: gratitudeShares,
        message: `Your gratitude has touched ${gratitudeShares * 3} people` // Ripple effect estimate
      };
    }
    
    return null;
  } catch (e) {
    return null;
  }
};

// State Selection Button
const StateButton = ({ state, isSelected, onSelect }) => {
  const Icon = state.icon;
  const isGolden = state.isGolden;
  
  // Special golden glow animation for Grateful state
  const goldenGlow = isGolden && isSelected ? {
    boxShadow: [
      '0 0 10px rgba(251,191,36,0.3)',
      '0 0 30px rgba(251,191,36,0.6)',
      '0 0 10px rgba(251,191,36,0.3)'
    ]
  } : isSelected ? {
    boxShadow: ['0 0 0px rgba(255,255,255,0)', '0 0 20px rgba(255,255,255,0.3)', '0 0 0px rgba(255,255,255,0)']
  } : {};
  
  return (
    <motion.button
      onClick={() => onSelect(state)}
      className={`relative flex flex-col items-center gap-1.5 p-3 rounded-2xl border-2 transition-all ${
        isSelected 
          ? `${state.bgColor} ${state.borderColor} shadow-lg` 
          : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
      }`}
      whileHover={{ scale: 1.05, y: -2 }}
      whileTap={{ scale: 0.98 }}
      data-testid={`alignment-state-${state.id}`}
    >
      <motion.div
        className={`w-10 h-10 rounded-full flex items-center justify-center ${
          isSelected ? `bg-gradient-to-br ${state.color}` : 'bg-white/10'
        }`}
        animate={goldenGlow}
        transition={{ duration: 1.5, repeat: isSelected ? Infinity : 0 }}
      >
        <Icon size={20} className={isSelected ? 'text-white' : 'text-white/60'} />
      </motion.div>
      <span className={`text-xs font-medium ${isSelected ? state.textColor : 'text-white/60'}`}>
        {state.label}
      </span>
    </motion.button>
  );
};

// Recommendation Card
const RecommendationCard = ({ recommendation, onClick, delay = 0, isGolden = false }) => {
  const Icon = recommendation.icon;
  
  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      whileHover={{ scale: 1.02, x: 5 }}
      whileTap={{ scale: 0.98 }}
      className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
        isGolden 
          ? 'bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/20 hover:border-amber-500/30'
          : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
      }`}
      data-testid={`alignment-recommendation-${recommendation.id}`}
    >
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
        isGolden 
          ? 'bg-gradient-to-br from-amber-400/30 to-yellow-500/30'
          : 'bg-gradient-to-br from-violet-500/30 to-indigo-500/30'
      }`}>
        <Icon size={22} className={isGolden ? 'text-amber-400' : 'text-violet-400'} />
      </div>
      <div className="flex-1">
        <p className="text-white font-medium">{recommendation.label}</p>
        <p className="text-white/50 text-sm">{recommendation.sublabel}</p>
      </div>
      <ArrowRight size={18} className={isGolden ? 'text-amber-400/60' : 'text-white/40'} />
    </motion.button>
  );
};

// Main Alignment Signal Component
const AlignmentSignal = ({ isOpen, onClose, onNavigate }) => {
  const [selectedState, setSelectedState] = useState(null);
  const [showRecommendations, setShowRecommendations] = useState(false);
  
  // Get pattern insights
  const insights = useMemo(() => getAlignmentInsights(), []);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedState(null);
      setShowRecommendations(false);
    }
  }, [isOpen]);

  // Handle state selection
  const handleStateSelect = (state) => {
    setSelectedState(state);
    // Show recommendations after a brief delay for smooth transition
    setTimeout(() => {
      setShowRecommendations(true);
    }, 300);
  };

  // Handle recommendation selection
  const handleRecommendationSelect = (recommendation) => {
    // Save the alignment state for tracking
    try {
      const alignmentHistory = JSON.parse(localStorage.getItem('reclaim_alignment_history') || '[]');
      alignmentHistory.push({
        state: selectedState?.id,
        recommendation: recommendation.id,
        timestamp: new Date().toISOString()
      });
      // Keep last 30 days of history
      const recent = alignmentHistory.slice(-90);
      localStorage.setItem('reclaim_alignment_history', JSON.stringify(recent));
      localStorage.setItem('reclaim_last_alignment', new Date().toDateString());
    } catch (e) {
      console.error('Error saving alignment:', e);
    }

    // Navigate to the recommended activity
    onNavigate(recommendation.route, recommendation.action);
    onClose();
  };

  // Skip alignment check
  const handleSkip = () => {
    localStorage.setItem('reclaim_last_alignment', new Date().toDateString());
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      >
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-[#0a0a1a]/95 backdrop-blur-md"
        />

        {/* Content */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="relative w-full max-w-md max-h-[90vh] overflow-y-auto"
        >
          {/* Skip button */}
          <motion.button
            onClick={handleSkip}
            className="absolute -top-2 right-0 p-2 text-white/40 hover:text-white/60 transition-colors z-10"
            whileHover={{ scale: 1.1 }}
            data-testid="alignment-skip-btn"
          >
            <X size={24} />
          </motion.button>

          {/* Header */}
          <div className="text-center mb-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-violet-500/30 to-indigo-500/30 flex items-center justify-center"
            >
              <Heart size={28} className="text-violet-400" />
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xl font-bold text-white mb-2"
            >
              How are you feeling within?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-white/50 text-sm"
            >
              Take a moment to check in with yourself
            </motion.p>
            
            {/* Pattern insight (if available) */}
            {insights && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-3 px-4 py-2 rounded-full bg-white/5 inline-flex items-center gap-2"
              >
                {insights.isGratitude ? (
                  <>
                    <Gift size={14} className="text-amber-400" />
                    <span className="text-amber-400/80 text-xs">{insights.message}</span>
                  </>
                ) : (
                  <>
                    <insights.state.icon size={14} className={insights.state.textColor} />
                    <span className={`${insights.state.textColor} text-xs opacity-80`}>{insights.message}</span>
                  </>
                )}
              </motion.div>
            )}
          </div>

          {/* State Selection - Show when no state is selected */}
          <AnimatePresence mode="wait">
            {!showRecommendations && (
              <motion.div
                key="states"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0, y: -20 }}
                className="grid grid-cols-3 gap-3 mb-6"
              >
                {ALIGNMENT_STATES.map((state, index) => (
                  <motion.div
                    key={state.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * index }}
                  >
                    <StateButton
                      state={state}
                      isSelected={selectedState?.id === state.id}
                      onSelect={handleStateSelect}
                    />
                  </motion.div>
                ))}
              </motion.div>
            )}

            {/* Recommendations - Show after state selection */}
            {showRecommendations && selectedState && (
              <motion.div
                key="recommendations"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Selected state summary */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`p-4 rounded-2xl ${selectedState.bgColor} border ${selectedState.borderColor} ${
                    selectedState.isGolden ? 'shadow-lg shadow-amber-500/20' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <motion.div 
                      className={`w-10 h-10 rounded-full bg-gradient-to-br ${selectedState.color} flex items-center justify-center`}
                      animate={selectedState.isGolden ? {
                        boxShadow: [
                          '0 0 10px rgba(251,191,36,0.3)',
                          '0 0 25px rgba(251,191,36,0.5)',
                          '0 0 10px rgba(251,191,36,0.3)'
                        ]
                      } : {}}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <selectedState.icon size={20} className="text-white" />
                    </motion.div>
                    <div>
                      <p className={`font-semibold ${selectedState.textColor}`}>{selectedState.label}</p>
                      <p className="text-white/70 text-sm">{selectedState.message}</p>
                    </div>
                  </div>
                  <p className="text-white/50 text-xs italic pl-13">
                    {selectedState.journeyMessage}
                  </p>
                </motion.div>

                {/* Change state button */}
                <button
                  onClick={() => {
                    setShowRecommendations(false);
                    setSelectedState(null);
                  }}
                  className="text-white/40 text-sm hover:text-white/60 transition-colors"
                  data-testid="alignment-change-state-btn"
                >
                  ← Change my state
                </button>

                {/* Recommendations label - special for Grateful */}
                <div className="space-y-3">
                  <p className="text-white/60 text-sm font-medium">
                    {selectedState.isGolden ? "Share the moment:" : "Recommended for you:"}
                  </p>
                  {selectedState.recommendations.map((rec, index) => (
                    <RecommendationCard
                      key={rec.id}
                      recommendation={rec}
                      onClick={() => handleRecommendationSelect(rec)}
                      delay={0.1 * index}
                      isGolden={selectedState.isGolden}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AlignmentSignal;
